// components/create/PassesSection.js
//
// What an invitation costs: nothing, or a share of the bill.
//
// ── Selling tickets is off ─────────────────────────────────────────────────
//
// The app hides its two ticketed types behind `TICKETED_PASSES` in
// src/config/flags.js, which is `false`. The website was still offering "Paid
// Event" and "Donation", which meant a host could build a paid invitation here
// that the app will not show them the controls for, hand over bank details and
// a GST number that lead nowhere, and end up with an invitation in a state the
// rest of the product has deliberately been taken out of.
//
// So the same two choices, with the same words:
//
//   Free          no payment required
//   Contribution  split costs with your guests
//
// A contribution is NOT a ticket sale. Nothing moves through the platform, no
// checkout, no bank account, no GST — the host's UPI id goes on the invitation
// and guests pay them directly. That is why the bank, GST and contact-person
// blocks that used to be here are gone rather than hidden: none of them apply
// to either remaining type.
//
// ── An invitation already selling tickets ──────────────────────────────────
//
// `passTypesFor` keeps an existing invitation's own type in the list when it
// is opened for edit, exactly as the app does. Without it, opening a legacy
// paid invitation would silently show it as Free, and saving would change what
// it is.

import React from 'react';
import '../../styles/PassesSection.scss';
import Icon from '../Icon';

const ALL_PASS_TYPES = [
  { id: 'free', label: 'Free', description: 'No payment required' },
  { id: 'fixed', label: 'Fixed Amount', description: 'Set a specific price', ticketed: true },
  { id: 'variable', label: 'Pay What You Can', description: 'Let guests choose amount', ticketed: true },
  { id: 'contribution', label: 'Contribution', description: 'Split costs with your guests' },
];

/** Mirrors the app's TICKETED_PASSES flag. Flip both together. */
const TICKETED_PASSES = false;

const PASS_TYPES = TICKETED_PASSES
  ? ALL_PASS_TYPES
  : ALL_PASS_TYPES.filter((t) => !t.ticketed);

const passTypesFor = (current) =>
  current && !PASS_TYPES.some((t) => t.id === current.id)
    ? [...PASS_TYPES, ALL_PASS_TYPES.find((t) => t.id === current.id) ?? current]
    : PASS_TYPES;

/** Rough shape of a UPI id: name@handle. */
const UPI_PATTERN = /^[\w.\-_]{2,256}@[a-zA-Z]{2,64}$/;

export default function PassesSection({
  passes = [],
  setPasses,
  globalPassType,
  setGlobalPassType,
  hostUpiId = '',
  setHostUpiId,
  isEditMode,
  hasSales,
}) {
  const types = passTypesFor(globalPassType);
  const isContribution = globalPassType?.id === 'contribution';
  const isLegacyTicketed = globalPassType?.id === 'fixed'
    || globalPassType?.id === 'variable'
    || globalPassType?.id === 'paid';

  const choose = (type) => {
    setGlobalPassType(type);

    if (type.id === 'free') {
      setPasses([]);
      if (setHostUpiId) setHostUpiId('');
      return;
    }

    if (type.id === 'contribution') {
      // One placeholder pass with no price, matching the app: a contribution
      // has no tiers, so there is nothing here for a host to configure.
      setPasses([
        {
          id: passes[0]?.id || Date.now().toString(),
          type,
          name: 'Contribution',
          price: '0',
          quantity: '',
        },
      ]);
    }
  };

  const upiLooksWrong =
    isContribution && hostUpiId.trim() !== '' && !UPI_PATTERN.test(hostUpiId.trim());

  return (
    <div className="passes-section">
      <div className="section-card">
        <label className="section-title">Cost</label>
        <p className="section-description">
          Free, or ask everyone to chip in. Money never passes through Invits —
          guests pay you directly.
        </p>

        <div className="option-group">
          {types.map((type) => (
            <button
              key={type.id}
              type="button"
              className={`option-button ${globalPassType?.id === type.id ? 'active' : ''}`}
              onClick={() => choose(type)}
              // A legacy ticketed invitation shows its type so the host can see
              // what it is, but switching TO one is not something this form can
              // carry out while selling is paused.
              disabled={type.ticketed || type.id === 'paid'}
            >
              <div className="option-content">
                <span className="option-label">{type.label}</span>
                <span className="option-description">{type.description}</span>
              </div>
              {globalPassType?.id === type.id && <Icon name="checkmark-circle" />}
            </button>
          ))}
        </div>

        {isLegacyTicketed && (
          <p className="passes-note passes-note--warn">
            This invitation sells tickets. Selling is paused, so its type cannot be
            changed here — everything else on this page can still be edited.
          </p>
        )}
      </div>

      {isContribution && (
        <div className="section-card">
          <label className="section-title" htmlFor="host-upi">Your UPI ID</label>
          <p className="section-description">
            Shown on the invitation so guests can send their share straight to you.
          </p>

          <div className="input-group">
            <input
              id="host-upi"
              type="text"
              className="text-input"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="yourname@upi"
              value={hostUpiId}
              onChange={(e) => setHostUpiId && setHostUpiId(e.target.value)}
              disabled={isEditMode && hasSales}
              aria-invalid={upiLooksWrong}
            />
            {upiLooksWrong && (
              <p className="passes-error">
                That does not look like a UPI ID — they are usually something like
                “name@okhdfcbank”.
              </p>
            )}
          </div>

          <p className="passes-note">
            Invits takes no cut and handles no money here. Nothing is collected
            automatically, and nobody is turned away for not having paid.
          </p>
        </div>
      )}
    </div>
  );
}
